import { useCallback, useEffect } from 'react';

import { AppProvider } from 'common/AppContext';
import { useSuperState } from 'helpers/hooks';
import { Authentication } from 'pages/auth';
import { Spinner } from 'react-bootstrap';
import { AppRouter } from 'AppRouter';
import ApiHelper from 'helpers/Api';

const api = new ApiHelper();

export const App = () => {

    const [state, setState] = useSuperState({
        env: null,
        error: null,
        isLoggedIn: false,
        loading: true,
        siteUrl: null,
        user: null
    });

    const { env, error, isLoggedIn, loading, siteUrl, user } = state;

    const fetchSiteBasics = useCallback(async () => {

        try {

            const { config } = await api.get('/api/site/basics');
            return { env: config.env, siteUrl: config.site_url };

        } catch (e) {

            return { error: e?.err || e?.message || String(e) };

        }

    }, []);

    const checkAuth = useCallback(async () => {

        try {

            const data = await api.post('/api/auth/check');
            return { isLoggedIn: !!data?.authenticated, user: data?.user || null };

        } catch (e) {

            return { isLoggedIn: false, user: null };

        }

    }, []);

    const tryMSLogin = useCallback(() => {

        // Open the auth flow in a new window. The backend redirects through
        // Microsoft and lands on /authenticate/callback, which sets a
        // localStorage 'msAuth' key and closes itself. The storage event
        // listener below picks that up and re-verifies + reloads.
        const w = 560;
        const h = 700;
        const left = window.screenX + (window.outerWidth - w) / 2;
        const top = window.screenY + (window.outerHeight - h) / 2;

        // In dev (React on :3000) the proxy forwards /api/auth/* to the
        // backend; the backend then redirects to Microsoft and back to
        // /authenticate/callback (a React route) handled by AppRouter.
        window.open(
            '/api/auth/new/microsoft',
            'feature-tester-msauth',
            `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`
        );

    }, []);

    const logout = useCallback(async () => {

        try {

            await api.post('/api/auth/logout');

        } catch (_) { /* ignore */ }
        window.location.reload();

    }, []);

    useEffect(() => {

        Promise.all([fetchSiteBasics(), checkAuth()]).then(([site, auth]) => {

            setState({ ...site, ...auth, loading: false });

        });

    }, [fetchSiteBasics, checkAuth, setState]);

    useEffect(() => {

        const onStorage = async event => {

            if (event.key !== 'msAuth') return;

            const auth = await checkAuth();
            if (auth.isLoggedIn) window.location.reload();

        };

        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);

    }, [checkAuth]);

    if (loading) {

        return (
            <div className="d-flex flex-column align-items-center justify-content-center vh-100 gap-3">
                <Spinner variant="primary" />
                <span className="text-muted">
                    Loading…
                </span>
            </div>
        );

    }

    if (error) {

        return (
            <div className="d-flex flex-column align-items-center justify-content-center vh-100 gap-3 p-4">
                <h4 className="text-danger">
                    Failed to start
                </h4>
                <pre className="text-muted">
                    {error}
                </pre>
            </div>
        );

    }

    const context = { api, env, logout, siteUrl, tryMSLogin, user };

    return (
        <AppProvider value={context}>
            {isLoggedIn ? <AppRouter /> : <Authentication />}
        </AppProvider>
    );

};
